import { CheckCircle2, CircleAlert, Info, TriangleAlert, X } from 'lucide-react'
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { NotificationContext } from './notification-context'
import './notifications.css'

export type AppNotificationVariant = 'default' | 'error' | 'info' | 'success' | 'warning'

interface EnqueueNotificationOptions {
  readonly autoHideDuration?: number
  readonly variant?: AppNotificationVariant
}

interface AppNotification {
  readonly duration: number
  readonly id: number
  readonly message: ReactNode
  readonly variant: AppNotificationVariant
}

interface NotificationProviderProps extends PropsWithChildren {
  readonly autoHideDuration?: number
  readonly maxSnack?: number
}

const notificationIcons = {
  default: Info,
  error: CircleAlert,
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
} as const

function NotificationToast({
  notification,
  onClose,
}: {
  readonly notification: AppNotification
  readonly onClose: (id: number) => void
}) {
  const Icon = notificationIcons[notification.variant]

  return (
    <div
      className="appNotificationToast"
      data-variant={notification.variant}
      role={notification.variant === 'error' ? 'alert' : 'status'}
    >
      <Icon aria-hidden="true" className="appNotificationIcon" />
      <div className="appNotificationMessage">{notification.message}</div>
      <button
        aria-label="Dismiss notification"
        className="appNotificationClose"
        onClick={() => onClose(notification.id)}
        type="button"
      >
        <X aria-hidden="true" />
      </button>
    </div>
  )
}

export function NotificationProvider({
  autoHideDuration = 3500,
  children,
  maxSnack = 4,
}: NotificationProviderProps) {
  const nextId = useRef(0)
  const [notifications, setNotifications] = useState<AppNotification[]>([])

  const handleClose = useCallback((id: number) => {
    setNotifications((current) => current.filter((item) => item.id !== id))
  }, [])

  const enqueueSnackbar = useCallback(
    (message: ReactNode, options: EnqueueNotificationOptions = {}) => {
      nextId.current += 1
      const id = nextId.current
      const notification: AppNotification = {
        duration: options.autoHideDuration ?? autoHideDuration,
        id,
        message,
        variant: options.variant ?? 'default',
      }

      setNotifications((current) => [...current, notification].slice(-maxSnack))
      window.setTimeout(() => handleClose(id), notification.duration)
      return id
    },
    [autoHideDuration, handleClose, maxSnack],
  )

  const contextValue = useMemo(() => ({ enqueueSnackbar }), [enqueueSnackbar])
  const viewport = notifications.length && typeof document !== 'undefined'
    ? createPortal(
        <div aria-label="Notifications" className="appNotificationViewport">
          {notifications.map((notification) => (
            <NotificationToast
              key={notification.id}
              notification={notification}
              onClose={handleClose}
            />
          ))}
        </div>,
        document.body,
      )
    : null

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {viewport}
    </NotificationContext.Provider>
  )
}
